import React, { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Tooltip } from 'react-tooltip';
import { ChevronLeftIcon, PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import TrackingAPI from 'api/TrackingAPI';
import void_ava from 'assets/gap_avatar.svg';
import token_icon from "assets/token.svg";
import { SSEEventType } from 'constants/sse';
import useSSEEvent from 'hooks/useSSEEvent';
import { clone } from 'ramda';
import { useMappedState } from 'redux-react-hook';
import { useGetTokenCostsQuery } from 'services/api/tokenApi';
import { useLazyGetCurrentUserQuery } from 'services/api/usersApi';
import styled, { css, keyframes } from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';
import { isBlank } from 'utils/storeUtils';

import { useGenerateAvatarQueuedMutation, useIsLegalMutation } from '../../../services/api/generateScenarioApi';
import PictureModal from '../../modals/PictureModal';
export const CharacterProfiles = props => {
  const {
    hasSubscription = false,
    initialPlayerDescription = '',
    initialCharactersDescription = {},
    initialCharactersAvatars = {},
    initialCharactersAppearance = {},
    onBack = Function.prototype,
    onContinue = Function.prototype,
    onGenerate = Function.prototype,
    onOpenSubscriptionModal = Function.prototype,
    onChange = Function.prototype,
    onOpenBuyTokens = Function.prototype
  } = props;

  const [playerDescription, setPlayerDescription] = useState(initialPlayerDescription);
  const [charactersDescription, setCharactersDescription] = useState(initialCharactersDescription);
  const [charactersAvatars, setCharactersAvatars] = useState(initialCharactersAvatars);
  const [charactersAppearance, setCharactersAppearance] = useState(initialCharactersAppearance);
  const [loading, setLoading] = useState(false);
  const [currentLoadingAvatarIndex, setCurrentLoadingAvatarIndex] = useState(null);
  const [avatarRefreshKeys, setAvatarRefreshKeys] = useState({}); // Ключи для пересборки изображений
  const [generatingAvatars, setGeneratingAvatars] = useState({});
  const [legalCharactersDescription, setLegalCharactersDescription] = useState(
    new Array(Object.keys(initialCharactersDescription)?.length).fill(undefined)
  );
  const [pictureModal, setPictureModal] = useState(undefined);
  const [avatarErrors, setAvatarErrors] = useState({});


  const [generateAvatarQueued] = useGenerateAvatarQueuedMutation();
  const [isLegalRequest] = useIsLegalMutation();

  const { data: avatarGenerationData } = useSSEEvent(SSEEventType.AVATAR_GENERATED);
  const [fetchUser] = useLazyGetCurrentUserQuery();

  const { currentUser } = useMappedState(
    useCallback(state => {
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
      };
    }, [])
  );

  const { data: tokenCosts } = useGetTokenCostsQuery(undefined, {
    selectFromResult: ({ data }) => ({ data: data ?? null })
  });

  const avatarGenerationCostCost = tokenCosts?.avatarGenerationCost;
  const tokenBalance = currentUser?.tokenBalance ?? 0;

  useEffect(() => {
    if (avatarGenerationData?.status === 'completed') {
      setAvatarRefreshKeys(prev => {
        const result = { ...prev };
        Object.keys(charactersAvatars).forEach(key => {
          result[key] = (result[key] || 0) + 1;
        });
        return result;
      });
      fetchUser();
      setCurrentLoadingAvatarIndex(null);
      setGeneratingAvatars(prev => {
        const result = { ...prev };
        Object.keys(charactersAvatars).forEach(key => {
          result[key] = false;
        });
        return result;
      });
      onGenerate();
    }
  }, [avatarGenerationData]);

  const handleImageError = (e, key) => {
    e.target.src = void_ava;
    setAvatarErrors(prev => ({ ...prev, [key]: true }));
  };

  const processAvatarGeneration = async (characterIndex, characterKey) => {
    TrackingAPI.trackEvent('generate_avatar');
    setGeneratingAvatars(prev => ({ ...prev, [characterKey]: true }));

    let isLegal = legalCharactersDescription[characterIndex];
    if (isLegal === undefined) {
      const { data: checkedIsLegal } = await isLegalRequest({ backstory: charactersDescription[characterKey] });
      isLegal = checkedIsLegal;

      setLegalCharactersDescription(prev => {
        const updatedLegal = [...prev];
        updatedLegal[characterIndex] = isLegal;
        return updatedLegal;
      });

      if (!isLegal) {
        errorAlert(
          'Restricted content. Terms of use violation. Please try different character description.'
        );
        setCurrentLoadingAvatarIndex(null);
        return;
      }

      const { data } = await generateAvatarQueued({ appearance: charactersDescription[characterKey], name: characterKey });

      if (data?.url) {
        setCharactersAvatars(prev => ({
          ...prev,
          [characterKey]: data.url
        }));
        setAvatarRefreshKeys(prev => ({
          ...prev,
          [characterKey]: (prev[characterKey] || 0) + 1
        }));
      }

      if (data?.appearance) {
        setCharactersAppearance(prev => ({
          ...prev,
          [characterKey]: data.appearance
        }));
      }
      setCurrentLoadingAvatarIndex(null);
    } else {
      if (isLegal) {

        const { data } = await generateAvatarQueued({ appearance: charactersDescription[characterKey], name: characterKey });

        if (data?.url) {
          setCharactersAvatars(prev => ({
            ...prev,
            [characterKey]: data.url
          }));
          setAvatarRefreshKeys(prev => ({
            ...prev,
            [characterKey]: (prev[characterKey] || 0) + 1
          }));
        }

        if (data?.appearance) {
          setCharactersAppearance(prev => ({
            ...prev,
            [characterKey]: data.appearance
          }));
        }
        setCurrentLoadingAvatarIndex(null);
      } else {
        errorAlert(
          'Restricted content. Terms of use violation. Please try different character description.'
        );
        setCurrentLoadingAvatarIndex(null);
        return;
      }
    }

    onGenerate();
  };

  const handleGenerateNewAvatar = (characterIndex, characterKey) => {
    if (currentLoadingAvatarIndex !== null) return;

    if (!hasSubscription) {
      onOpenSubscriptionModal();
      return;
    }
    if (tokenBalance < avatarGenerationCostCost) {
      onOpenBuyTokens();
      return;
    }

    setCurrentLoadingAvatarIndex(characterIndex);
    processAvatarGeneration(characterIndex, characterKey);
  };

  const handleChangeCharacterDescription = (textAreaEvent, characterIndex, characterKey) => {
    const newDescription = clone(charactersDescription);
    newDescription[characterKey] = textAreaEvent.target.value;

    const legal = [...legalCharactersDescription];
    legal[characterIndex] = undefined;

    setLegalCharactersDescription(legal);
    setCharactersDescription(newDescription);
  };

  const handleMoveToNextStep = async () => {
    if (loading) {
      return;
    }

    for (const name in charactersDescription) {
      if (charactersDescription[name]?.trim() === '') {
        errorAlert(`Enter ${name}'s description`);
        return;
      }
    }

    setLoading(true);
    await onContinue(playerDescription, charactersDescription, charactersAvatars, charactersAppearance);
    setLoading(false);
  };

  useEffect(() => {
    onChange(playerDescription, charactersDescription, charactersAvatars, charactersAppearance);
  }, [playerDescription, charactersDescription, charactersAvatars, charactersAppearance]);


  return (
    <Wrapper>
      <UserInfoRow>
        <Label>Description of your character</Label>
        <SelfDescriptionTextArea
          placeholder="Information that characters know about you. You may leave it blank"
          value={playerDescription}
          onChange={evt => {
            setPlayerDescription(evt.target.value);
          }}
        />
      </UserInfoRow>

      <CharacterProfilesSection>
        <LabelCharacters>Description of characters</LabelCharacters>
        {Object.keys(charactersDescription)?.map((key, index) => {
          const isLoadingAvatar = currentLoadingAvatarIndex === index || generatingAvatars[key];
          const hasAvatar = !isBlank(charactersAvatars[key]);
          const refreshKey = avatarRefreshKeys[key] || 0;
          const isDisabledGenerateButton = (currentLoadingAvatarIndex !== null && currentLoadingAvatarIndex !== index) || isLoadingAvatar;

          const isGapAvatar = charactersAvatars[key] === 'gap';

          return (
            <Row key={key + index}>
              <div className="flex flex-wrap items-center justify-between gap-2 md:gap-0">
                <div className="flex items-center gap-3">
                  {!hasAvatar ? (
                    <div className="h-12 w-12 rounded-full bg-[rgb(19,19,19)]" />
                  ) : (
                    <AvatarWrapper isPendingGeneration={isLoadingAvatar} hasError={avatarErrors[key] && !isGapAvatar}>
                      <img
                        key={`${key}-avatar-${refreshKey}`}
                        src={charactersAvatars[key]}
                        className={`h-12 w-12 ${isGapAvatar ? 'cursor-default' : 'cursor-pointer'} rounded-full object-cover`}
                        onClick={() => !isLoadingAvatar && !isGapAvatar && setPictureModal(charactersAvatars[key])}
                        onError={(e) => handleImageError(e, key)}
                      />
                    </AvatarWrapper>
                  )}
                  <CharacterNameSection>
                    <span>{key}</span>
                    <GenerationButtonWrapper >
                      <GenerationButton
                        className={`flex flex-col gap-1 ${!isLoadingAvatar
                          ? 'cursor-pointer'
                          : 'cursor-default text-gray-500'
                          }`}
                        onClick={() => handleGenerateNewAvatar(index, key)}
                        isDisabled={isDisabledGenerateButton}
                      >
                        {isLoadingAvatar ? (
                          <Spinner />
                        ) : (
                          <>
                            <PlusIcon width={16} height={16} />
                            <GenerateAvatarLabel>{isGapAvatar ? 'Generate avatar' : 'Regenerate avatar'}</GenerateAvatarLabel>
                            <TokenSection><span>{avatarGenerationCostCost}</span>
                              <TokenIcon src={token_icon} />
                            </TokenSection>
                          </>
                        )}
                      </GenerationButton>
                      <div className="flex items-center gap-2">
                        <QuestionMarkCircleIcon
                          className="h-6 w-6 text-gray-100"
                          data-tooltip-id={`generate_new_avatar_${key}`}
                        />
                        <Tooltip
                          id={`generate_new_avatar_${key}`}
                          content={
                            <p className="max-w-[calc(100vw-40px)] break-words">
                              Generates a photo of the character based on the description
                            </p>
                          }
                        />
                      </div>
                    </GenerationButtonWrapper>
                  </CharacterNameSection>
                </div>

                <TextArea
                  placeholder={`${key}'s description`}
                  value={charactersDescription[key]}
                  onChange={event => handleChangeCharacterDescription(event, index, key)}
                  disabled={currentLoadingAvatarIndex === index}
                />
                <Description>{`Describe the character ${key}`}</Description>
              </div>
            </Row>
          );
        })}
      </CharacterProfilesSection>
      <ButtonPlaceholder>
        {isMobile && (
          <MobileBackButton
            type="button"
            onClick={onBack}
          >
            <ChevronLeftIcon />
          </MobileBackButton>
        )}
        {!isMobile && (
          <TransparentButton
            type="button"
            onClick={onBack}
          >
            Back
          </TransparentButton>
        )}

        <OrangeButton isMobile={isMobile} onClick={handleMoveToNextStep}>
          {loading ? <Spinner /> : 'Continue'}
        </OrangeButton>
      </ButtonPlaceholder>
      {
        pictureModal && (
          <PictureModal onClose={() => setPictureModal(null)} src={pictureModal} />
        )
      }
    </Wrapper >
  );
};

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  47% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  53% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  95% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
`;

const GenerationButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 16px;
`;



const AvatarWrapper = styled.div`
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  
  ${props => (props.isPendingGeneration || props.hasError) && css`
    background: #131313;
    
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
    }
  `}
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const CharacterNameSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  height: 100%;
  padding: 0;
`;

const CharacterProfilesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 54px;
`;

const GenerationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #A9A9A9;
  flex-direction: row;
  border: 1px solid #282828;
  border-radius: 8px;
  padding: 4px;
  transition: opacity 0.2s, filter 0.2s;
  
  ${props => props.isDisabled && css`
    opacity: 0.5;
    filter: grayscale(50%);
    cursor: not-allowed !important;
    pointer-events: none;
  `}
`;

const GenerateAvatarLabel = styled.span`
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;

  @media (max-width: 377px) {
    font-size: 12px;
  }
`;

const TokenSection = styled.span`
  background-color: #282828;
  border-radius: 4px;
  padding: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Nunito Sans;

  & span {
    font-weight: 700;
    font-size: 12px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: right;
    color: #fff;
  }
`;

const TokenIcon = styled.img`
  width: 12px;
  height: 12px;
`;

const Label = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 700;
  line-height: 18px;
  color: #FFFFFF;
  margin-bottom: 16px;
`;

const LabelCharacters = styled(Label)`
  margin-bottom: 0px;
`;

const Description = styled.span`
  color: rgba(169, 169, 169, 1);
  font-family: Nunito Sans;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  text-align: left;
  margin-top: 8px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid rgba(50, 50, 50, 0.5);
  border-radius: 12px;
  padding: 20px;
`;

const UserInfoRow = styled(Row)`
  border: none;
  padding: 0;
`;

const TextArea = styled.textarea`
  background-color: rgba(19, 19, 19, 1) !important;
  border: 1px solid rgba(50, 50, 50, 0.5);
  border-radius: 16px;
  color: white;
  min-height: 74px;
  max-height: 140px;
  outline: none;
  font-family: Nunito Sans;
  padding: 16px;
  width: 100%;
  resize: vertical;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;

  :focus {
    border-color: #ff7e55;
    box-shadow: none !important;
  }
  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: rgba(19, 19, 19, 0.5) !important;
  }
`;

const SelfDescriptionTextArea = styled(TextArea)`
  margin-bottom: 16px;
`;

const ButtonPlaceholder = styled.div`
  padding-top: 8px;
  width: 100%;
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    margin-top: auto;
    background: #1A1A1A;
    padding-top: 10px;
    padding-bottom: 16px;
    bottom: 0;
    position: fixed;
    width: 100%;
    z-index: 10;
  }
`;

const OrangeButton = styled.button`
  background-color: rgba(255, 126, 85, 1);
  width: ${props => props.isMobile ? '66%' : '100%'};
  padding: 8px 24px;
  border-radius: 100px;
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
  display: flex;
  justify-content: center;
`;

const TransparentButton = styled(OrangeButton)`
  background-color: transparent;
  border: 1px solid rgba(255, 126, 85, 1);
  color: rgba(255, 126, 85, 1);
  box-shadow: none;
`;

const MobileBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 40px;
  background: #1A1A1A;
  border-radius: 100px;
  color: white;
  border: 1px solid #FF7E55;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;
